import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    TableContainer,
    Tooltip,
    Menu,
    MenuItem,
    Select,
    InputBase,
    Pagination,
    CircularProgress,
} from '@mui/material';
import { Edit, Delete, MoreVert } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiUrl from '../../Config';

const DisplayBooks = () => {
    const navigate = useNavigate();
    const [books, setBooks] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedBook, setSelectedBook] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    const open = Boolean(anchorEl);

    const handleMenuClick = (event, book) => {
        setAnchorEl(event.currentTarget);
        setSelectedBook(book);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedBook(null);
    };

    const getAllBooks = async () => {
        setLoading(true)
        try {
            const response = await axios.get(`${apiUrl}/books/getallbooksadmin?page=${page}&limit=${limit}&searchTerm=${searchTerm}`);

            const data = response.data;

            setBooks(data.books || []);
            setTotalPages(data.totalPages || 1);
            setTotalItems(data.totalItems || 0);
        } catch (error) {
            console.log('Error fetching books:', error);
            const message = error?.response?.data?.message || error.message || 'Failed to fetch testimonials'
            setError(message)
        } finally {
            setLoading(false)
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${apiUrl}/books/deletebook/${id}`);
            getAllBooks();
            handleMenuClose();
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    useEffect(() => {
        getAllBooks();
    }, [page, limit]);

    const handleSearch = () => {
        setPage(1);
        getAllBooks();
    };

    return (
        <Box p={2}>
            <Typography variant="h4" my={2.5}>
                Products List
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <InputBase
                        placeholder="Search Product"
                        sx={{
                            border: '1px solid #ccc',
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            width: { xs: '220px', md: 300 },
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button variant="outlined" onClick={handleSearch}>
                        Search
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Rows per page:</Typography>
                    <Select
                        size="small"
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                    </Select>

                    <Button variant="contained" onClick={() => navigate('/create-edit-books')}>
                        Add Product
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>In Stock</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Publishing Year</TableCell>
                                    <TableCell>Is Active</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {books.map((book) => (
                                    <TableRow key={book.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {book.bookImages?.[0] && (
                                                    <img
                                                        src={book.bookImages[0].imageUrl}
                                                        alt="thumbnail"
                                                        width={40}
                                                        height={40}
                                                        style={{ objectFit: 'cover', borderRadius: 4 }}
                                                    />
                                                )}
                                                <Typography variant="h6" fontWeight={400}>
                                                    {book.title}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{book.code || '—'}</TableCell>
                                        <TableCell>{book.availability ? 'Available' : 'Not Available'}</TableCell>
                                        <TableCell>₹{book.price}</TableCell>
                                        <TableCell>{book.publishingYear}</TableCell>
                                        <TableCell>{book.isActive === true ? "Yes" : "No"}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuClick(e, book)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {books.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No books found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box mt={3} display="flex" justifyContent="center">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            showFirstButton
                            showLastButton
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    height: 30,
                                    minWidth: 30,
                                    fontSize: '1rem',
                                    marginTop: '10px'
                                }
                            }}
                        />
                    </Box>

                    <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                        <MenuItem
                            onClick={() => {
                                navigate('/create-edit-books', { state: { book: selectedBook } });
                                handleMenuClose();
                            }}
                        >
                            <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(selectedBook.id)}>
                            <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                        </MenuItem>
                    </Menu>
                </>
            )}

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default DisplayBooks;
